import React from 'react';
import styled from 'styled-components';
import { AlbumPlayer } from './AlbumPlayer';
import { AlbumInfo } from './AlbumInfo';

interface Track {
    title: { type: 'text'; content: string }[];
    artist: { type: 'text'; content: string }[];
    album: { type: 'text'; content: string }[];
    duration: string;
    src: string;
}

interface ListInfo {
    type: 'album';
    name: string;
    numTracks: number;
    duration: string;
    releaseDate?: string;
    genre?: string;
    imageSrc?: string;
}

interface Credit {
    name: string;
    role: string;
}

interface AlbumSectionProps {
    albumId: string;
    tracks: Track[];
    listInfo: ListInfo;
    albumTitle: string;
    albumDescription: string;
    credits: Credit[];
    purchaseLink: string;
    playlistPosition: 'left' | 'right';
    backgroundColor: string;
}

const SectionWrapper = styled.div<{ $backgroundColor: string }>`
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    display: flex;
    justify-content: center;
    width: 100%;
`;

const SectionContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    width: 100%;
    max-width: 1800px;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: 1fr;
    }
`;

const PlaylistSide = styled.div<{ $position: 'left' | 'right' }>`
    order: ${({ $position }) => ($position === 'left' ? 1 : 2)};
    padding: ${({ theme }) => theme.spacing.xxl};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 20;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        order: 1;
        padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    }
`;

const InfoSide = styled.div<{ $position: 'left' | 'right' }>`
    order: ${({ $position }) => ($position === 'left' ? 2 : 1)};
    padding: ${({ theme }) => theme.spacing.xxl};
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        order: 2;
        padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    }
`;

const ContentWrapper = styled.div`
    max-width: 600px;
    width: 100%;
`;

export const AlbumSection: React.FC<AlbumSectionProps> = ({
    albumId,
    tracks,
    listInfo,
    albumTitle,
    albumDescription,
    credits,
    purchaseLink,
    playlistPosition,
    backgroundColor,
}) => {
    return (
        <SectionWrapper $backgroundColor={backgroundColor}>
            <SectionContainer>
                <PlaylistSide $position={playlistPosition}>
                    <ContentWrapper>
                        <AlbumPlayer albumId={albumId} tracks={tracks} listInfo={listInfo} />
                    </ContentWrapper>
                </PlaylistSide>
                <InfoSide $position={playlistPosition}>
                    <ContentWrapper>
                        <AlbumInfo
                            title={albumTitle}
                            description={albumDescription}
                            credits={credits}
                            purchaseLink={purchaseLink}
                        />
                    </ContentWrapper>
                </InfoSide>
            </SectionContainer>
        </SectionWrapper>
    );
};
